/**
 * Registro de mapas.
 *
 * Todo arquivo `src/world/maps/*.js` que faça `export default` de um objeto com
 * `id` e `build` vira um mapa jogável automaticamente — não existe lista para
 * manter em dia, e um mapa novo não obriga a mexer em nenhum arquivo de outro
 * agente. O contrato do objeto está documentado no topo de `src/world/MapKit.js`.
 *
 * O glob é EAGER de propósito. `import()` dinâmico com caminho montado em tempo
 * de execução já derrubou a publicação uma vez (404 silencioso, ver DEPLOY.md);
 * e o `Level.init()` é síncrono, então esperar por um import assíncrono ali
 * significaria subir o jogo sem mapa. Quatro mapas de dados custam poucos KB.
 */
const modulos = import.meta.glob('./*.js', { eager: true });

const registro = new Map();
for (const [caminho, mod] of Object.entries(modulos)) {
  const def = mod?.default;
  if (!def || !def.id || typeof def.build !== 'function') continue;   // index.js e rascunhos caem aqui
  if (registro.has(def.id)) {
    console.warn(`[maps] id duplicado "${def.id}" em ${caminho} — o primeiro vence`);
    continue;
  }
  registro.set(def.id, def);
}

/** Id usado quando a URL não pede nenhum. É o mapa que está no ar. */
export const MAPA_PADRAO = 'blackout';

/**
 * Mapas que existem mas ainda NÃO aparecem no menu.
 *
 * Some do seletor e continua respondendo a `?map=<id>`. A distinção é o ponto:
 * um mapa em construção precisa ser aberto e medido o tempo todo — `dump-level`,
 * `measure`, `test-rota-alta` e `shoot` todos abrem a página com `--map` — e
 * tirá-lo do REGISTRO faria `getMapa()` cair no padrão em silêncio, com todas
 * essas ferramentas medindo o Blackout enquanto imprimem o nome do outro mapa.
 * Tirá-lo só da LISTA DO MENU faz exatamente o que se quer: quem joga não vê,
 * quem está construindo continua trabalhando.
 *
 * Esvaziar este conjunto é o último passo de um mapa novo.
 */
const EM_CONSTRUCAO = new Set(['vila']);

/** Lista para o menu: só o que a interface precisa, sem arrastar a geometria. */
export const MAPAS = [...registro.values()]
  .filter((m) => !EM_CONSTRUCAO.has(m.id))
  .map((m) => ({ id: m.id, nome: m.nome || m.id, regiao: m.regiao || '' }))
  .sort((a, b) => (a.id === MAPA_PADRAO ? -1 : b.id === MAPA_PADRAO ? 1 : a.nome.localeCompare(b.nome, 'pt-BR')));

/** Definição completa. Cai no padrão quando o id não existe — nunca devolve nada. */
export function getMapa(id) {
  return registro.get(id) || registro.get(MAPA_PADRAO) || registro.values().next().value || null;
}

/**
 * Qual mapa carregar, em ordem de autoridade:
 *
 *   1. `?map=<id>` na URL — é o que uma ferramenta de teste e um link
 *      compartilhado usam, e tem que vencer sempre;
 *   2. o último escolhido no menu (localStorage `blackout:mapa`, gravado por
 *      `src/ui/Identity.js`) — sem isto, trocar de mapa duraria até a próxima
 *      recarga, que é justamente quando a troca acontece;
 *   3. o padrão.
 *
 * A leitura do disco é feita aqui, e não pela interface, porque o `Level.init`
 * é síncrono e roda antes de qualquer tela existir. A chave está escrita nos
 * dois arquivos de propósito — não há um lugar comum entre `world/` e `ui/` que
 * não invente uma dependência pior que a duplicação de uma string.
 */
export function mapaDaURL() {
  try {
    const q = new URLSearchParams(globalThis.location?.search || '');
    const daUrl = q.get('map') || q.get('mapa');
    if (daUrl) return daUrl;
  } catch { /* fora do navegador */ }
  try {
    const salvo = globalThis.localStorage?.getItem('blackout:mapa');
    if (salvo && registro.has(salvo)) return salvo;
  } catch { /* modo privado, ou node */ }
  return MAPA_PADRAO;
}
